/**
 * BSCPF-6584. The supervisor's decline reasons, in one place.
 *
 * Employer verification sends a fixed **code** rather than prose, because the PM sees the
 * choice verbatim on the applicant report and it needs a stable vocabulary. That means the
 * same four reasons are rendered twice — as radio options on the employer-facing page, and
 * as a label on the report — and the two must not drift. Hence one module.
 *
 * The codes are the API's contract: they are validated against
 * `BoomScreen::PremiumRentalHistory::Survey::EMPLOYMENT_DECLINE_REASONS` server-side, so a
 * value added here without the matching API constant is rejected with
 * `invalid_decline_reason`.
 *
 * Rental history is deliberately not modelled here: it has always sent the prose itself as
 * the reason and stays free-form.
 */
export const EMPLOYER_DECLINE_REASON = {
  THIRD_PARTY_SERVICE: "third_party_service",
  NOT_EMPLOYED_HERE: "not_employed_here",
  NOT_THE_RIGHT_PERSON: "not_the_right_person",
  OTHER: "other"
} as const;

type EmployerDeclineReason =
  (typeof EMPLOYER_DECLINE_REASON)[keyof typeof EMPLOYER_DECLINE_REASON];

export const EMPLOYER_DECLINE_REASON_LABEL: Record<
  EmployerDeclineReason,
  string
> = {
  [EMPLOYER_DECLINE_REASON.THIRD_PARTY_SERVICE]:
    "We only verify employment through a third-party service (e.g. The Work Number)",
  [EMPLOYER_DECLINE_REASON.NOT_EMPLOYED_HERE]:
    "This person doesn't work here and has no offer from us",
  [EMPLOYER_DECLINE_REASON.NOT_THE_RIGHT_PERSON]:
    "I'm not the right person to answer this",
  [EMPLOYER_DECLINE_REASON.OTHER]: "Other"
};

/** Radio options for the employer-facing decline modal, in the ticket's order. */
export const EMPLOYER_DECLINE_REASON_OPTIONS = (
  Object.keys(EMPLOYER_DECLINE_REASON_LABEL) as EmployerDeclineReason[]
).map((value) => ({ value, label: EMPLOYER_DECLINE_REASON_LABEL[value] }));

/**
 * What the PM reads on the report. Falls back to the raw value so a reason recorded
 * before a label existed still renders as something rather than vanishing.
 */
export const employerDeclineReasonLabel = (
  reason?: string | null
): string | undefined => {
  if (!reason) return undefined;

  return (
    EMPLOYER_DECLINE_REASON_LABEL[reason as EmployerDeclineReason] ?? reason
  );
};
